/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { PopperProps, Typography } from '@mui/material';
import { theme } from '../../assets/style/them';
import { DATEPICKER_DATE_ONLY_FORMAT, DATEPICKER_DATE_TIME_FORMAT } from 'utils/utils';
import DateRangeField from './DateRangeField';
import { useDateRangePickerStyles } from './DateRangePickerStyles';

interface DateRangePickerProps {
    dateTo?: string;
    dateFrom?: string;
    dateFormat?: string;
    isToShowTime?: boolean;
    isMinDate?: boolean;
    isMaxDate?: boolean;
    minDateSubstrAmount?: number; //
    timeInterval?: number;
    minTime?: Date;
    maxTime?: Date;
    minDate?: Date;
    onDateChange: ({ dateFrom, dateTo }: { dateFrom: moment.Moment; dateTo: moment.Moment }) => void;
    column?: boolean;
    wrap?: boolean;
    isCustomLabel?: boolean;
    // Shows "From"/"To" labels beside each input instead of the single "Date" title above the
    // whole group.
    showFieldLabels?: boolean;
    popperPlacement?: PopperProps['placement'];
    inputWidth?: number;
    disabled?: boolean;
}

const DateRangePicker = (props: DateRangePickerProps) => {
    const classes = useDateRangePickerStyles();
    const {
        onDateChange,
        dateTo,
        dateFrom,
        dateFormat,
        isToShowTime,
        isMinDate,
        isMaxDate,
        timeInterval,
        minDateSubstrAmount,
        column,
        wrap,
        minTime,
        maxTime,
        minDate,
        isCustomLabel,
        showFieldLabels,
        popperPlacement,
        inputWidth,
        disabled,
    } = props;

    const { t } = useTranslation();
    const fieldFormat = dateFormat ?? (isToShowTime ? DATEPICKER_DATE_TIME_FORMAT : DATEPICKER_DATE_ONLY_FORMAT);
    const fieldMaxDate = isMaxDate === false ? undefined : moment();
    const fieldMinDate = isMinDate
        ? minDate
            ? moment(minDate)
            : moment(dateTo).clone().subtract(minDateSubstrAmount, 'day')
        : undefined;
    const handleChangeStartDate = (date: moment.Moment | null) => {
        if (!date) return;
        const startDate = isToShowTime ? moment(date) : moment(date).startOf('day');
        let endDate = moment(dateTo);

        if (startDate.isAfter(endDate)) endDate = startDate.clone().endOf('day');

        onDateChange({ dateFrom: startDate, dateTo: endDate });
    };

    const handleChangeEndDate = (date: moment.Moment | null) => {
        if (!date) return;
        let startDate = moment(dateFrom);
        const endDate = isToShowTime ? moment(date) : moment(date).endOf('day');

        if (endDate.isBefore(startDate)) startDate = endDate.clone().startOf('day');

        onDateChange({ dateFrom: startDate, dateTo: endDate });
    };

    return (
        <div
            className={classes.root}
            style={{ flexDirection: !column ? 'row' : 'column', flexWrap: wrap ? 'wrap' : 'nowrap' }}
        >
            <div className={classes.startFieldContainer}>
                {!isCustomLabel && !showFieldLabels ? (
                    <label className={classes.title} htmlFor="dateSelect">
                        <Typography noWrap variant="subtitle1">
                            {`${t('app.date')}`}
                        </Typography>
                    </label>
                ) : null}
                <div className={classes.fieldRow} style={{ marginBottom: wrap ? theme.spacing(1) : 0 }}>
                    {showFieldLabels ? (
                        <Typography
                            noWrap
                            variant="body2"
                            className={classes.fieldLabel}
                            style={{ minWidth: column ? 34 : 'auto' }}
                        >
                            {`${t('app.from')}`}
                        </Typography>
                    ) : null}
                    <DateRangeField
                        value={moment(dateFrom)}
                        onChange={handleChangeStartDate}
                        format={fieldFormat}
                        placeholder={`${t('app.from')}:`}
                        showTime={isToShowTime}
                        maxDate={fieldMaxDate}
                        minDate={fieldMinDate}
                        minTime={minTime ? moment(minTime) : undefined}
                        maxTime={maxTime ? moment(maxTime) : undefined}
                        timeInterval={timeInterval}
                        disabled={disabled}
                        popperPlacement={popperPlacement}
                        inputWidth={inputWidth}
                        rangeStart={moment(dateFrom)}
                        rangeEnd={moment(dateTo)}
                    />
                </div>
            </div>

            <div
                className={classes.fieldContainer}
                style={{ marginTop: column ? theme.spacing(2) : 0, marginBottom: wrap ? theme.spacing(1) : 0 }}
            >
                <div className={classes.fieldRow}>
                    {showFieldLabels ? (
                        <Typography
                            noWrap
                            variant="body2"
                            className={classes.fieldLabel}
                            style={{ minWidth: column ? 34 : 'auto' }}
                        >
                            {`${t('app.to')}`}
                        </Typography>
                    ) : null}
                    <DateRangeField
                        value={moment(dateTo)}
                        onChange={handleChangeEndDate}
                        format={fieldFormat}
                        placeholder={`${t('app.to')}:`}
                        showTime={isToShowTime}
                        maxDate={fieldMaxDate}
                        minDate={fieldMinDate}
                        minTime={minTime ? moment(minTime) : undefined}
                        maxTime={maxTime ? moment(maxTime) : undefined}
                        timeInterval={timeInterval}
                        disabled={disabled}
                        popperPlacement={popperPlacement}
                        inputWidth={inputWidth}
                        rangeStart={moment(dateFrom)}
                        rangeEnd={moment(dateTo)}
                    />
                </div>
            </div>
        </div>
    );
};

export default DateRangePicker;
